'use client';

import React, { useState, useCallback, useEffect } from 'react';
import type {
  Range,
  RangeFilterDropdownProps,
} from '@src/v3/interfaces/components/coreUI/filterDropdown/rangeFilterDropdown';
import Slider from '@src/v3/components/coreUI/slider';
import TextField from '@src/v3/components/coreUI/textField';
import { formatNumberWithCommas } from '@src/v3/utils/helper';

const RangeFilterDropdown: React.FC<RangeFilterDropdownProps> = ({
  initialRange = { min: 0, max: 50000 },
  onRangeChange,
  minRange = 0,
  maxRange = 50000,
  title = '',
  subTitle = '',
  isCurrency = false,
  unit = '',
  containerClassName = '',
}) => {
  const [range, setRange] = useState<Range>(initialRange);
  // Local input state for free typing (stores raw string values)
  const [minInput, setMinInput] = useState<string>(String(initialRange.min));
  const [maxInput, setMaxInput] = useState<string>(String(initialRange.max));

  // Update range and input state when initialRange prop changes
  useEffect(() => {
    setRange(initialRange);
    setMinInput(String(initialRange.min));
    setMaxInput(String(initialRange.max));
  }, [initialRange]);
  // Handle slider change
  const handleSliderChange = useCallback(
    (min: number, max: number) => {
      const newRange = { min, max };
      setRange(newRange);
      setMinInput(String(min));
      setMaxInput(String(max));
      onRangeChange?.(newRange);
    },
    [onRangeChange]
  );

  // Validate and apply input values
  const applyInputValues = useCallback(() => {
    let newMin = parseInt(minInput.replace(/,/g, ''), 10) || 0;
    let newMax = parseInt(maxInput.replace(/,/g, ''), 10) || 0;

    // Ensure min is not negative
    if (newMin < 0) newMin = 0;
    // Ensure max is not negative
    if (newMax < 0) newMax = 0;
    // Cap min at maxRange
    if (newMin > maxRange) newMin = maxRange;
    // Cap max at maxRange
    if (newMax > maxRange) newMax = maxRange;
    // Ensure min doesn't exceed max
    if (newMin > newMax) {
      newMin = newMax;
    }

    const newRange = { min: newMin, max: newMax };
    setRange(newRange);
    setMinInput(String(newMin));
    setMaxInput(String(newMax));
    onRangeChange?.(newRange);
  }, [minInput, maxInput, maxRange, onRangeChange]);

  // Handle input blur
  const handleInputBlur = () => {
    applyInputValues();
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyInputValues();
      e.currentTarget.blur();
    }
  };

  // Format display value for inputs (show actual values with commas)
  // For max value, show "50,000+" when value >= maxRange
  const formatDisplayValue = (
    value: string,
    showCurrency: boolean,
    isMaxValue = false
  ) => {
    const numValue = parseInt(value.replace(/,/g, ''), 10);
    if (isNaN(numValue)) return showCurrency ? '$0' : '0';

    // Show "+" suffix for max value when it's >= maxRange
    if (isMaxValue && numValue === maxRange) {
      const formattedNumber = formatNumberWithCommas(maxRange);
      return showCurrency ? `$${formattedNumber}+` : `${formattedNumber}+`;
    }

    const formattedNumber = formatNumberWithCommas(numValue);
    return showCurrency ? `$${formattedNumber}` : formattedNumber;
  };

  // Get slider value (capped at maxRange for visual display)
  const getSliderValue = (value: number, max: number) => {
    return Math.min(value, max);
  };

  return (
    <div className={containerClassName}>
      {/* Header */}
      <div className="flex flex-col gap-y-9">
        <div className="flex flex-col">
          {title && (
            <span className="text-xl font-medium text-black-dark">{title}</span>
          )}
          {subTitle && (
            <span className="text-base text-gray-dark">{subTitle}</span>
          )}
        </div>

        {/* Dual Range Slider */}

        <div className="flex flex-col gap-y-6">
          <Slider
            isMulti
            variant="blue"
            min={minRange}
            max={maxRange}
            extraClasses="!px-0"
            value={[
              getSliderValue(range.min, maxRange),
              getSliderValue(range.max, maxRange),
            ]}
            onChange={(value: number | number[]) => {
              const min = Array.isArray(value) ? value[0] : value;
              const max = Array.isArray(value) ? value[1] : value;
              handleSliderChange(min, max);
            }}
          />

          <div className="flex justify-between cursor-default">
            <div className="flex flex-col items-center gap-y-2">
              <span className="text-xs font-normal text-gray-dark">
                Minimum
              </span>
              <TextField
                type="text"
                inputMode="numeric"
                showLabel={false}
                containerClassName="!w-[120px]"
                inputClassName="!h-[50px] !border-2 !border-gray-300 text-center !text-lg font-normal text-black-dark"
                value={formatDisplayValue(minInput, isCurrency)}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  setMinInput(rawValue);
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                endAdornment={
                  unit ? (
                    <span className="text-gray-dark flex-shrink-0">{unit}</span>
                  ) : undefined
                }
              />
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <span className="text-xs font-normal text-gray-500">Maximum</span>
              <TextField
                type="text"
                inputMode="numeric"
                showLabel={false}
                containerClassName="!w-[120px]"
                inputClassName="!h-[50px] !border-2 !border-gray-300 text-center !text-lg font-normal text-black-dark"
                value={formatDisplayValue(maxInput, isCurrency, true)}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  setMaxInput(rawValue);
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                endAdornment={
                  unit ? (
                    <span className="text-gray-dark flex-shrink-0">{unit}</span>
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeFilterDropdown;
